import type { FC } from 'react'
import React, { memo, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import { Button } from '@frontend/pole-ui/lib/components/Button'
import { Icon } from '@frontend/pole-ui/lib/components/Icon'
import { QuestFillIcon } from '@frontend/pole-ui/lib/components/Icon/svg/QuestFillIcon'
import type { CompanySuggestion } from '@pole-api/lib-integration-dadata-api/lib/models/CompanySuggestion'
import type { FioSuggestion } from '@pole-api/lib-integration-dadata-api/lib/models/FioSuggestion'
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha'
import classNames from 'classnames'
import { SuggestionCompany, SuggestionFio } from '@/components/composed/modals/AuthModal/components'
import { pushOpenModalAuthConfirmGtmEvent } from '@/components/composed/modals/AuthModal/components/gtmHelpers'
import {
  getSuggestionFioValue,
  getSuggestionValue,
  onSuggestionCompanySelected,
  onSuggestionsCompanyFetchRequested,
  onSuggestionsFioFetchRequested,
} from '@/components/composed/modals/AuthModal/components/helpers'
import authMessages from '@/components/composed/modals/AuthModal/messages.json'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { Autosuggest, TextInput } from '@/components/ui/ReactHookFormFields'
import { CAPTCHA_SITE_KEY } from '@/constants/captcha'
import { sendingSmsRequestFailed, sendRegistrationRequest } from '@/logic/auth/actions'
import { getError, getRootState, isAuthenticated, isFetching, isRegistrationSuccessful } from '@/logic/auth/reducer'
import { useCaptcha } from '@/logic/captcha/useCaptcha'
import { useData } from '@/logic/data'
import { injectMessages } from '@/logic/translation/utils'
import type { RootState } from '@/redux/interfaces'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { makeAllFieldsTouched } from '@/utils/fields'
import { normalizePhoneNumber } from '@/utils/formatUtils'
import { formatPhone } from '@/utils/helpers'
import { useMount } from '@/utils/hooks'
import { autosuggestOrganizationProfileValidator } from '@/utils/validators'
import { RegistrationDocument } from './components/RegistrationDocument'
import { REGISTRATION_DOCUMENTS_DATA } from './components/RegistrationDocument/constants'
import { messages } from './messages'
import type { IRegistrationFormProps, IRegistrationFormState } from './types'
import './styles.scss'

injectMessages(authMessages)

const mapState = (state: RootState) => ({
  isRegistrationSuccessful: isRegistrationSuccessful()(state),
  isAuthenticated: isAuthenticated()(state),
  isFetching: isFetching()(state),
  registrationError: getError()(state),
  rootState: getRootState()(state),
})

const RegistrationFormComponent: FC<IRegistrationFormProps> = ({
  className,
  headerClassName,
  titleClassName,
  fieldsClassName,
  formClassName,
  phoneNumberClassName,
  checkboxesClassName,
  buttonsClassName,
  phoneNumber,
  goBackButtonModifiers = ['outline-gray', 'small'],
  submitButtonModifiers = ['green', 'small'],
  submitButtonText = messages.button.submit,
  fullNameFieldModifiers,
  organizationProfileFieldModifiers,
  emailFieldModifiers,
  goBack,
  afterRegistrationComplete,
  shouldSkipSelectRoleStep = false,
}) => {
  const { data: documents = [] } = useData<TLegalDocuments>('legalDocuments')

  const { openAuthModal } = useAuthenticateUser()

  const { captchaVisible, captchaToken, isCaptchaEnabled, setCaptchaToken, handleChallengeHidden, setCaptchaVisible, setIsCaptchaEnabled } =
    useCaptcha()

  const dispatch = useDispatch()
  const state = useSelector(mapState)

  const form = useForm<IRegistrationFormState>({
    mode: 'onChange',
  })

  const handleSuccessCaptcha = useCallback(
    (token: string) => {
      const updateCaptchaToken = () => {
        setCaptchaToken(token)

        if (token) {
          const requestFormValues = form.getValues()

          dispatch(
            sendRegistrationRequest({
              formValues: {
                ...requestFormValues,
                email: requestFormValues.email || undefined,
                phone: normalizePhoneNumber(phoneNumber),
                captchaToken: token,
              },
              isSendingAgain: true,
            }),
          )
        }
      }

      updateCaptchaToken()
    },

    [dispatch, form, phoneNumber, setCaptchaToken],
  )

  const onSubmit = (values: IRegistrationFormState) => {
    if (isCaptchaEnabled && !captchaToken) {
      setCaptchaVisible(true)

      return
    }

    // Экшен для установки в стор состояния регистрации
    dispatch(sendingSmsRequestFailed({ shouldRegisterUser: true }))

    if (isCaptchaEnabled && captchaToken) {
      dispatch(
        sendRegistrationRequest({
          formValues: {
            ...values,
            email: values.email || undefined,
            phone: normalizePhoneNumber(phoneNumber),
            captchaToken,
          },
          isSendingAgain: true,
        }),
      )

      pushOpenModalAuthConfirmGtmEvent(state.rootState)

      return
    }

    dispatch(
      sendRegistrationRequest({
        formValues: {
          ...values,
          email: values.email || undefined,
          phone: normalizePhoneNumber(phoneNumber),
        },
        isSendingAgain: true,
      }),
    )

    pushOpenModalAuthConfirmGtmEvent(state.rootState)
  }

  const handleSubmitAndTouchAllFields: Parameters<typeof form.handleSubmit>[0] = values => {
    onSubmit(values)
    makeAllFieldsTouched(form)
  }

  const handleSubmitErrorAndTouchAllFields: Parameters<typeof form.handleSubmit>[1] = () => {
    makeAllFieldsTouched(form)
  }

  useEffect(() => {
    if (state.isRegistrationSuccessful) {
      openAuthModal({
        initialValues: {
          ...form.getValues(),
          phone: normalizePhoneNumber(phoneNumber),
        },
        codeAlreadySent: true,
        shouldSkipSelectRoleStep,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, state.isRegistrationSuccessful])

  useMount(() => {
    pushOpenModalAuthConfirmGtmEvent(state.rootState)
  })

  useEffect(() => {
    if (state.isAuthenticated) {
      afterRegistrationComplete?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated])

  return (
    <div className={classNames(className, 'registrationForm')}>
      <div className={classNames(headerClassName, 'registrationForm__header')}>
        <h4 className={classNames(titleClassName, 'registrationForm__title')}>
          {messages.header.title.part1} <span className={phoneNumberClassName}>{formatPhone(phoneNumber)}</span>{' '}
          {messages.header.title.part2}
        </h4>
        <p>{messages.header.text}</p>
      </div>

      <form className={formClassName} onSubmit={form.handleSubmit(handleSubmitAndTouchAllFields, handleSubmitErrorAndTouchAllFields)}>
        <div className={classNames(fieldsClassName, 'registrationForm__fields')}>
          <Autosuggest<FioSuggestion, IRegistrationFormState>
            control={form.control}
            name="fullName"
            getSuggestionValue={getSuggestionFioValue}
            onSuggestionsFetchRequested={onSuggestionsFioFetchRequested()}
            suggestionComponent={SuggestionFio}
            onSuggestionSelected={async ({ suggestion }) => suggestion.value}
            allowRawValue
            isRequired
            customInputProps={{
              placeholder: messages.form.fullName,
              id: 'registrationFormFullName',
              name: 'fullName',
              modifiers: fullNameFieldModifiers,
            }}
            rules={{
              required: messages.errors.required,
            }}
          />

          <Autosuggest<CompanySuggestion, IRegistrationFormState>
            name="organizationProfile"
            control={form.control}
            suggestionComponent={SuggestionCompany}
            getSuggestionValue={getSuggestionValue}
            onSuggestionsFetchRequested={onSuggestionsCompanyFetchRequested(form.setValue, true)}
            onSuggestionSelected={onSuggestionCompanySelected}
            customValidator={autosuggestOrganizationProfileValidator()}
            shouldAlwaysRenderSuggestions
            autosearchValue="test"
            shouldHighlightFirstSuggestion
            isRequired
            customInputProps={{
              placeholder: messages.form.organization,
              name: 'organizationProfile',
              id: `registrationFormOrganizationProfile`,
              modifiers: organizationProfileFieldModifiers,
              bubblingTooltipProps: {
                children: (
                  <div className="row">
                    <Icon component={<QuestFillIcon />} style={{ color: EColor.GREEN }} />
                  </div>
                ),
                tooltipProps: {
                  placement: 'top-end',
                },
                content: (
                  <>
                    <h5 className="space-holder8">{messages.tooltip.title}</h5>
                    <p className="text_small">{messages.tooltip.text}</p>
                  </>
                ),
              },
            }}
            rules={{
              required: messages.errors.inn,
            }}
          />

          <TextInput
            control={form.control}
            name="email"
            type="email"
            id="authModalEmail"
            placeholder={messages.form.email}
            modifiers={emailFieldModifiers}
            isRequired
            rules={{
              required: messages.errors.required,
            }}
          />
        </div>

        <div className={classNames(checkboxesClassName, 'registrationForm__checkboxes')}>
          {REGISTRATION_DOCUMENTS_DATA.map(document => (
            <RegistrationDocument control={form.control} key={document.name} {...document} documents={documents} />
          ))}
        </div>

        {state.registrationError && (
          <p className="registrationForm__error">
            <span>{state.registrationError.text}</span>
          </p>
        )}

        <div className={classNames(buttonsClassName, 'registrationForm__buttons')}>
          <Button modifiers={goBackButtonModifiers} onClick={goBack} isDisabled={state.isFetching}>
            {messages.button.goBack}
          </Button>

          <Button
            type="submit"
            className="registrationForm__submitButton"
            modifiers={submitButtonModifiers}
            isDisabled={state.isFetching}
            loaderProps={{ isVisible: state.isFetching }}
          >
            {submitButtonText}
          </Button>
        </div>
        {isCaptchaEnabled && (
          <InvisibleSmartCaptcha
            sitekey={CAPTCHA_SITE_KEY}
            onSuccess={handleSuccessCaptcha}
            visible={captchaVisible}
            language="ru"
            onChallengeHidden={handleChallengeHidden}
            shieldPosition="bottom-right"
            hideShield={false}
            onNetworkError={() => setIsCaptchaEnabled(false)}
          />
        )}
      </form>
    </div>
  )
}

export const RegistrationForm = memo(RegistrationFormComponent)
